import { StatusId, OperationBCI, CommandeRang, CommandeArticle, HistoriqueEvent } from '../types';

// Derivations PURES de l'onglet Offre acheteur-BC : articles formates, etat de
// presentation (bon de commande / refus), flags d'affichage du statut. Aucune
// dependance React. Gating par id de statut (les noms en DB peuvent changer).

const parseRang = (value: string | number): number => parseInt(String(value), 10);

const getCmd = (operation: OperationBCI, rang: number): CommandeRang | undefined =>
  (operation.commandes || []).find((c) => parseRang(c.rang) === rang);

const toNumber = (value: number | string | undefined, fallback: number): number => {
  if (value === undefined || value === '') return fallback;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isNaN(parsed) ? fallback : parsed;
};

// --- Articles de l'offre (tableau financier, lecture seule) -------------------
export interface OffreArticle {
  id: string | number;
  code: string;
  nom: string;
  description: string;
  unite: string;
  quantite: number;
  prixUnitaire: number;
  totalHT: number;
}

export const formatOffreArticles = (operation: OperationBCI, rang: number): OffreArticle[] => {
  const cmd = getCmd(operation, rang);
  const articles: CommandeArticle[] = cmd?.articles || [];

  return articles.map((raw, index) => {
    const catalogue = raw.catalogue;
    const articleData = catalogue?.article;

    if (!catalogue || !articleData) {
      return {
        id: index,
        code: `Article ${index + 1}`,
        nom: 'Article non défini',
        description: '',
        unite: 'Unité',
        quantite: 1,
        prixUnitaire: 0,
        totalHT: 0,
      };
    }

    // L'info lot/departement peut vivre nichee (operation.operation.*) OU au top-level
    // (operation.* cote acheteur direct), exactement comme le sous-titre (OperationOffre.tsx:327-329).
    // Sans le fallback top-level, la reference tombait sur les defauts -> "00 000 X 1".
    const departementId =
      operation.operation?.lot_departement?.departement?.id ??
      operation.lot_departement?.departement?.id ??
      '00';
    const lotId =
      operation.operation?.lot_technique?.id ?? operation.lot_technique?.id ?? '000';
    const articleCode = catalogue.code ?? index;
    const reference = `${departementId.toString().padStart(2, '0')} ${lotId
      .toString()
      .padStart(3, '0')} ${articleCode} ${rang}`;

    return {
      id: articleData.id ?? catalogue.id ?? index,
      code: reference,
      nom: catalogue.nom || `Article ${index + 1}`,
      description: catalogue.description || '',
      unite: catalogue.unite || 'Unité',
      quantite: toNumber(articleData.quantity ?? articleData.quantite, 1),
      prixUnitaire: toNumber(articleData.prix_unitaire ?? articleData.prix, 0),
      totalHT: toNumber(articleData.prix_total ?? articleData.totalHT, 0),
    };
  });
};

export const totalOffreArticles = (articles: OffreArticle[]): number =>
  articles.reduce((sum, a) => sum + a.totalHT, 0);

// --- Etat de presentation de l'offre du rang ----------------------------------
export interface OffreRefusInfo {
  date: string;
  refusedBy: 'acheteur' | 'prestataire';
  acheteurNom: string;
  prestataireNom: string;
  rang: number;
  isExpired: boolean;
  isCancelled: boolean;
}

export interface OffreState {
  showBonCommande: boolean;
  showRefusMessage: boolean;
  refusInfo: OffreRefusInfo | null;
}

const refusedByFromHistorique = (cmd: CommandeRang): 'acheteur' | 'prestataire' => {
  const refusEvent: HistoriqueEvent | undefined = cmd.historique?.find(
    (h) =>
      h.prefix?.toLowerCase().includes('refus') || h.message?.toLowerCase().includes('refus'),
  );
  if (refusEvent) {
    if (
      refusEvent.message?.toLowerCase().includes('acheteur') ||
      refusEvent.prefix?.toLowerCase().includes('acheteur') ||
      refusEvent.type_compte === 'acheteur'
    ) {
      return 'acheteur';
    }
    return 'prestataire';
  }
  return cmd.type_compte === 'acheteur' ? 'acheteur' : 'prestataire';
};

export const computeOffreState = (operation: OperationBCI, rang: number): OffreState => {
  const cmd = getCmd(operation, rang);
  if (!cmd) return { showBonCommande: false, showRefusMessage: false, refusInfo: null };

  const statusId = cmd.id_status;
  const isAccepted = statusId === StatusId.Valide;
  const isRefused = statusId === StatusId.Refuse;
  const isExpired = statusId === StatusId.Expire;
  const isCancelled = statusId === StatusId.Annule;
  const isRefusedOrExpiredOrCancelled = isRefused || isExpired || isCancelled;

  const acheteurNom = operation.organisme?.nom || operation.acheteur?.nom || 'Acheteur';
  const prestataireNom = cmd.contrat?.entite?.nom || 'Prestataire';

  return {
    showBonCommande: isAccepted,
    showRefusMessage: isRefusedOrExpiredOrCancelled,
    refusInfo: isRefusedOrExpiredOrCancelled
      ? {
          date: cmd.updated_at || '',
          refusedBy: isCancelled ? 'acheteur' : refusedByFromHistorique(cmd),
          acheteurNom,
          prestataireNom,
          rang,
          isExpired,
          isCancelled,
        }
      : null,
  };
};

// --- Flags d'affichage du statut ----------------------------------------------
export interface OffreStatusFlags {
  isAccepted: boolean;
  isRefused: boolean;
  isExpired: boolean;
  isPending: boolean;
}

// "En attente de l'acceptation du prestataire" : strictement par id, sur les
// statuts acheteur reels (AttenteAcceptationRang1/2/3 = 3/5/6). On NE matche aucun
// nom : ils peuvent etre d'anciens libelles, des statuts MS ou cote prestataire.
export const computeOffreStatusFlags = (
  operation: OperationBCI,
  _rang: number,
): OffreStatusFlags => {
  const statusId = operation.status?.id;

  const isPending =
    statusId === StatusId.AttenteAcceptationRang1 ||
    statusId === StatusId.AttenteAcceptationRang2 ||
    statusId === StatusId.AttenteAcceptationRang3;

  return {
    isAccepted: statusId === StatusId.Valide,
    isRefused: statusId === StatusId.Refuse,
    isExpired: statusId === StatusId.Expire,
    isPending,
  };
};
